import { minBy } from 'lodash';

/**
 * Combines two arrays of ranges such that it cuts the overlapping part from the first range and appends the second range
 * @example
 * |---|   |-----| |--------|   |----|
 *            |---| |-----|   |----|
 *=============becomes================
 * |---|   |--|---| |-----|   |----|
 */
export function sliceCombineRanges<T extends { start: number; stop: number }>(
  acc: T[],
  curr: ReadonlyArray<T>
): T[] {
  if (curr.length === 0) {
    return acc;
  }
  const minStart = minBy(curr, v => v.start)!.start; // minBy returns undefined if curr is empty, but we know it's not

  const beforeMinStart = acc.filter(item => item.stop < minStart);

  const atMinStart = acc
    .filter(item => item.start < minStart && item.stop >= minStart)
    .map(item => ({ ...item, stop: minStart }));

  return [...beforeMinStart, ...atMinStart, ...curr];
}

/**
 * Combines two arrays of ranges such that it cuts the overlapping part from the first range and appends the second range
 * @example
 * |---|   |-----| |--------|   |----|
 *            |---| |-----|   |----|
 *=============becomes================
 * |---|   |--|---| |-----|   |----|
 */
export function sliceCombineDateRanges<T extends { start: Date; stop: Date }>(
  acc: T[],
  curr: ReadonlyArray<T>
): T[] {
  if (curr.length === 0) {
    return acc;
  }
  const minStart = minBy(curr, v => v.start.getTime())!.start.getTime(); // minBy returns undefined if curr is empty, but we know it's not

  const beforeMinStart = acc.filter(item => item.stop.getTime() < minStart);

  const atMinStart = acc
    .filter(item => item.start.getTime() < minStart && item.stop.getTime() >= minStart)
    .map(item => ({ ...item, stop: new Date(minStart) }));

  const result = [...beforeMinStart, ...atMinStart, ...curr];

  return result;
}
